import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";
import useSelectedColorStore from "@store/register/useSelectedColor";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { Flex } from "antd";
import ToastAlert from "@components/alert/ToastAlert";
import {
  createOptionKey,
  isValidHex,
  normalizeHex,
  normalizedColorKey,
  getColorOptionIdentity,
} from "@utils/productClassification";
import { useImageAnalysisStore } from "@store/imageAnalysis/useImageAnalysisStore";
import ColorPickerPanel from "@pages/register/_components/ColorPickerPanel";
import { CustomOptionItem, CustomRadio, CustomCheck } from "@components/dropdown/CustomOption";

type Props = {
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
  mode?: "single" | "multi";
};
const ColorDropdown = ({
  name,
  open,
  setOpen,
  selectedValue,
  setSelectedValue,
  mode,
}: Props) => {
  const { getColorList } = useApiRequest();
  const { data: colorList } = useQuery({
    queryKey: ["colorList"],
    queryFn: getColorList,
  });
  const [focusName, setFocusName] = useState("");
  const [customNameKo, setCustomNameKo] = useState("");
  const [customNameEn, setCustomNameEn] = useState("");
  const [customNameJa, setCustomNameJa] = useState("");
  const [customHex, setCustomHex] = useState("#111111");
  const [isCustomMode, setIsCustomMode] = useState(false);

  const { control, setValue, watch } = useFormContext();

  // 촬영 등록은 store, 일반 등록은 image_list 폼 필드에서 사진을 가져온다.
  const { uploadedImageUrl } = useImageAnalysisStore();
  const formImages: string[] = watch("image_list") || [];
  const pickerImages = Array.from(
    new Set([...(uploadedImageUrl ? [uploadedImageUrl] : []), ...formImages]),
  ).filter(Boolean);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const theme = useTheme();

  const { color, setColor, setColorId, setColorOptionKey } = useSelectedColorStore();

  const { fields, append } = useFieldArray({
    control,
    name,
  });

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocusName(name);
    if (colorList?.length === 0) return;

    setOpen(!open);
  };

  const handleChange = (
    colorName: string,
    colorValue: string,
    colorId: number,
  ) => {
    setIsCustomMode(false);
    const existingColor = fields.find((field: any) => field.color_id === colorId) as any;
    const optionKey = existingColor?.option_key || createOptionKey();
    setOpen(false);
    setSelectedValue(colorName);
    setColor(colorName);
    setColorId(colorId);
    setColorOptionKey(getColorOptionIdentity({ option_key: optionKey }));

    if (mode === "single") {
      setColor(colorName); // Zustand 상태 저장
      setColorId(colorId);
      setSelectedValue(colorName);
      setValue(name, {
        color_id: colorId,
        option_key: optionKey,
        color_name: colorName,
        color_name_ko: colorName,
        hex_code: colorValue,
      });
    } else {
      // react-hook-form으로 append
      if (!existingColor) {
        append({
          color_name: colorName,
          color_name_ko: colorName,
          hex_code: colorValue,
          size_list: [],
          color_id: colorId,
          option_key: optionKey,
        });
      }
    }
  };

  const handleAddCustomColor = () => {
    try {
      const nameKo = customNameKo.trim();
      const nameEn = customNameEn.trim();
      if (!nameEn) {
        ToastAlert("색상명(영문)을 입력해주세요.");
        return;
      }
      const hex = normalizeHex(customHex);
      const key = normalizedColorKey(nameKo || nameEn, hex);
      const duplicated = fields.some(
        (field: any) => normalizedColorKey(field.color_name_ko || field.color_name, field.hex_code) === key,
      );
      if (duplicated) {
        ToastAlert("이미 추가된 색상입니다.");
        return;
      }
      const custom = {
        option_key: createOptionKey(),
        color_id: null,
        color_name: nameKo || nameEn,
        color_name_ko: nameKo,
        color_name_en: nameEn,
        color_name_ja: customNameJa.trim() || null,
        hex_code: hex,
        size_list: [],
      };
      if (mode === "single") {
        setValue(name, custom, { shouldValidate: true });
      } else {
        append(custom);
      }
      setColor(custom.color_name);
      setColorId(0);
      setColorOptionKey(custom.option_key);
      setSelectedValue(custom.color_name);
      setOpen(false);
      setIsCustomMode(false);
      setCustomNameKo("");
      setCustomNameEn("");
      setCustomNameJa("");
    } catch (error) {
      ToastAlert(error instanceof Error ? error.message : "색상 정보를 확인해주세요.");
    }
  };

  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName, name, setOpen]);

  // 페이지를 벗어날 때 색상 선택 초기화
  useEffect(() => {
    return () => {
      setColor("");
      setColorId(0);
      setColorOptionKey("");
      setSelectedValue("선택");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dropdownHeight, setDropdownHeight] = useState(420);

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openDownward = spaceBelow >= spaceAbove;
    // 피커 패널이 있어 목록만 있던 때보다 길다. 넓은 쪽으로 열고 여백 16px을 남긴다.
    const available = Math.max(240, (openDownward ? spaceBelow : spaceAbove) - 16);
    const height = Math.min(520, available);

    // RGB 3칸이 눌리지 않을 최소 폭을 확보하되, 화면 밖으로 나가지 않게 한다.
    const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 32);
    const left = Math.min(rect.left, window.innerWidth - width - 16);

    setDropdownHeight(height);
    setDropdownPos({
      top: openDownward
        ? rect.bottom + window.scrollY
        : rect.top + window.scrollY - height,
      left: Math.max(16, left),
      width,
    });
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={color === "" ? selectedValue : color}
        ref={buttonRef}>
        <Flex align="start" gap="1rem">
          {/* 선택한 컬러의 컬러 볼 렌더링 */}
          {colorList
            ?.filter((item: any) => item.name === color)
            .map((item: any) => (
              <ColorBall key={item.id} color={item.hex_code} />
            ))}
          {/* 컬러 name */}
          {color === "" ? selectedValue : color}
        </Flex>
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {open &&
        ReactDOM.createPortal(
          <SelectBox
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            maxHeight={dropdownHeight}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            <CustomOptionItem
              type="button"
              selected={isCustomMode}
              onClick={() => setIsCustomMode((on) => !on)}>
              <CustomRadio selected={isCustomMode} />
              직접 입력
              {isCustomMode && <CustomCheck>✓</CustomCheck>}
            </CustomOptionItem>

            {!isCustomMode &&
              colorList?.map((item: any, index: number) => (
                <OptionItem
                  type="button"
                  key={item.id}
                  last={index === colorList?.length - 1}
                  onClick={() => handleChange(item.name, item.hex_code, item.id)}>
                  <ColorBall color={item.hex_code} />
                  {item.name}
                </OptionItem>
              ))}

            {isCustomMode && (
            <CustomSection>
              <FieldLabel>색상명 (한글)</FieldLabel>
              <CustomInput
                value={customNameKo}
                onChange={(event) => setCustomNameKo(event.target.value)}
                placeholder="예: 딥와인"
              />
              <CustomInput
                value={customNameEn}
                onChange={(event) => setCustomNameEn(event.target.value)}
                placeholder="Color name (English) *"
              />
              <CustomInput value={customNameJa} onChange={(event) => setCustomNameJa(event.target.value)} placeholder="色名 (日本語・선택)" />
              <ColorPickerPanel
                images={pickerImages}
                value={customHex}
                onChange={setCustomHex}
              />
              <CustomAddButton
                type="button"
                onClick={handleAddCustomColor}
                disabled={Boolean(customHex) && !isValidHex(customHex)}>
                직접 색상 추가
              </CustomAddButton>
            </CustomSection>
            )}
          </SelectBox>,
          document.body,
        )}
    </Container>
  );
};

export default ColorDropdown;

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",

    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

// motion.div는 모르는 prop을 DOM으로 흘리므로 shouldForwardProp으로 막는다.
const LAYOUT_PROPS = ["top", "left", "width", "maxHeight"];

const SelectBox = styled(motion.div, {
  shouldForwardProp: (prop) => !LAYOUT_PROPS.includes(prop as string),
})<{
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}>(({ theme, top, left, width, maxHeight }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: `${maxHeight}px`,
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  width: "100%",
  padding: "10px 30px 10px 20px",
  display: "flex",
  gap: "1rem",
  alignItems: "center",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.regular,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

const ColorBall = styled.div<{ color: string }>(({ color }) => ({
  width: "1.6rem",
  height: "1.6rem",
  borderRadius: "50%",
  backgroundColor: color,
  transition: "background-color 0.3s ease",
}));

const CustomSection = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  width: "100%",
  padding: "1.2rem",
  borderTop: `1px solid ${theme.color.border_gray}`,
  backgroundColor: theme.color.white,
}));

const FieldLabel = styled.label({ fontSize: "1.2rem", fontWeight: 600 });
const CustomInput = styled.input(({ theme }) => ({
  width: "100%",
  padding: "0.9rem",
  border: `1px solid ${theme.color.border_gray}`,
  fontSize: "1.3rem",
}));
const CustomAddButton = styled.button(({ theme }) => ({
  padding: "1rem",
  border: "none",
  backgroundColor: theme.color.black,
  color: theme.color.white,
  ":disabled": { backgroundColor: theme.color.light_gray, cursor: "not-allowed" },
}));
